import React, { useState, useCallback, useEffect, useMemo, Fragment } from 'react';
import { debounce } from 'lodash';
import { useNavigate } from 'react-router-dom';
import { Page, Layout, Box, BlockStack, Text, Link, Toast } from '@shopify/polaris';
import { useDispatch, useSelector } from 'react-redux';
import { getPriceUpdateLogAsync } from '../redux/priceUpdateLog/index';
import { getAllPriceUpdateLog } from '../redux/priceUpdateLog/slice';
import { getAllUserOnboarding } from '../redux/userOnboarding/slice';
import useOnboardingRedirect from '../redux/userOnBoardingRedirect';
import PriceUpdateLogTable from '../components/price-update-log/PriceUpdateLogTable';
import BannerComponent from '../components/common/Banner';
import Placeholder from '../components/common/Placeholder';
import '../styles/index.css';

const PriceUpdateLogPage = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { priceUpdateLogData, isLoading, pagination, updateMethod, autoInterval, isDeleteLoading } = useSelector(getAllPriceUpdateLog);
    const { userOnboardingData } = useSelector(getAllUserOnboarding);
    const [bannerVisible, setBannerVisible] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchValue, setSearchValue] = useState('');
    const [sortSelected, setSortSelected] = useState(['date desc']);
    const [selectedFilter, setSelectedFilter] = useState(0);
    const [active, setActive] = useState(false);
    const [toastContent, setToastContent] = useState('');
    const [refreshApiCall, setRefreshApiCall] = useState(false);

    useOnboardingRedirect(userOnboardingData);

    const autoPriceEnabled = updateMethod === 'Auto';

    const debouncedSearch = useMemo(
        () =>
            debounce(async (currentPage, searchValue, sortSelected, selectedFilter) => {
                dispatch(getPriceUpdateLogAsync({
                    page: currentPage,
                    search: searchValue,
                    sort: sortSelected[0] === 'date desc' ? 'desc' : 'asc',
                    status: selectedFilter === 1 ? 'Successful' : selectedFilter === 2 ? 'Failed' : selectedFilter === 3 ? 'Partially Successful' : ''
                }));
            }, 300),
        [dispatch]
    );

    useEffect(() => {
        debouncedSearch(currentPage, searchValue, sortSelected, selectedFilter);
        return () => {
            debouncedSearch.cancel();
        };
    }, [dispatch, currentPage, searchValue, sortSelected, selectedFilter, refreshApiCall]);

    useEffect(() => {
        const lastDismissed = localStorage.getItem('banner-dismissed-time');
        const currentTime = new Date().getTime();
        const oneDayInMs = 24 * 60 * 60 * 1000;

        if (lastDismissed && currentTime - lastDismissed < oneDayInMs) {
            setBannerVisible(false);
        }
    }, []);

    const handleSearchValueChange = useCallback(
        (value) => setSearchValue(value),
        [],
    );

    const handleSearchValueRemove = useCallback(() => setSearchValue(''), []);

    const handleDismiss = () => {
        const currentTime = new Date().getTime();
        localStorage.setItem('banner-dismissed-time', currentTime);
        setBannerVisible(false);
    };

    const toggleActive = useCallback((content) => {
        setActive((active) => !active)
        setToastContent(content);
    }, []);

    const toastMarkup = active ? (
        <Toast duration={2000} content={toastContent} onDismiss={toggleActive} />
    ) : null;

    const handleCallBack = (success, message) => {
        if (success) {
            toggleActive(message);
            setRefreshApiCall(!refreshApiCall);
        }
    };

    return (
        <Page>
            <Layout>
                <Layout.Section>
                    <Box padding='200'>
                        <BlockStack gap='100'>
                            <Text variant='headingMd'>Daily Logs Overview</Text>
                            <Text variant='bodySm' tone='subdued'>
                                Shows rate of change from first entry of data to today
                            </Text>
                        </BlockStack>
                    </Box>
                    {bannerVisible &&
                        <BannerComponent onDismiss={handleDismiss} tone={autoPriceEnabled ? 'success' : 'info'}>
                            {autoPriceEnabled ? (
                                <Fragment>
                                    Auto feature is active. Your prices will be updated every {autoInterval?.value} {autoInterval?.unit}.
                                </Fragment>
                            ) : (
                                <Fragment>
                                    <Text as='p' tone='subdued'>
                                        Activate the Auto Price Update feature for better performance. {' '}
                                        <Link onClick={() => navigate('/setting')}>
                                            Go to Setting
                                        </Link>{' '}
                                        and enable the Auto feature.
                                    </Text>
                                </Fragment>
                            )}
                        </BannerComponent>
                    }
                </Layout.Section>
                <Layout.Section>
                    <PriceUpdateLogTable
                        priceUpdateLogData={priceUpdateLogData}
                        pagination={pagination}
                        searchValue={searchValue}
                        handleSearchValueChange={handleSearchValueChange}
                        handleSearchValueRemove={handleSearchValueRemove}
                        setCurrentPage={setCurrentPage}
                        currentPage={currentPage}
                        loading={isLoading}
                        sortSelected={sortSelected}
                        setSortSelected={setSortSelected}
                        selectedFilter={selectedFilter}
                        setSelectedFilter={setSelectedFilter}
                        isDeleteLoading={isDeleteLoading}
                        handleCallBack={handleCallBack}
                    />
                </Layout.Section>
            </Layout>
            <Placeholder />
            {toastMarkup}
        </Page>
    );
};

export default PriceUpdateLogPage;
